/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

export const ROUTES = {
  HOME: "/",
  LOCATIONS: "/locations",
  SERVICES: "/services",
  SERVICES_HOME: "/booking",
  CLASSES: "/classes",
  PROVIDERS: "/providers",
  ADDONS: "/addons",
  PRODUCTS: "/products",
  DATETIME: "/datetime",
  DETAILS: "/details",
  PRODUCTS_FOR_SALE_CATEGORIES: "/products-for-sale-categories",
  PRODUCTS_FOR_SALE: "/products-for-sale",
  ADDITIONAL_FIELDS: "/details/additional-fields",
  CHECKOUT: "/booking/checkout",
  BOOKING_STATUS: "/booking/status",
  BOOKING_CONFIRMED: "/booking/status/confirmed",
  BOOKING_CANCELLED: "/booking/status/cancelled",
  BOOKING_PENDING: "/booking/status/pending",
  BOOKING_DELAY: "/booking/status/delay",
  CART_ITEM_STATUS: "/cart/status",
  CART_ITEM_CONFIRMED: "/cart/status/confirmed",
  CART_ITEM_CANCELLED: "/cart/status/cancelled",
  CART_ITEM_PENDING: "/cart/status/pending",
  CART_ITEM_DELAY: "/cart/status/delay",
  BOOKING_RESCHEDULED: "/booking/status/rescheduled",
  CLIENT_PROFILE: "/client/profile",
  CLIENT_EDIT: "/client/edit",
  CLIENT_PASSWORD_CHANGE: "/client/password-change",
  CLIENT_BOOKINGS: "/client/bookings",
  RESCHEDULE_BOOKING: "/client/reschedule-booking/:id",
  CLIENT_PACKAGES: "/client/packages",
  CLIENT_INVOICES: "/client/invoices",
  CLIENT_PAYMENTS: "/client/payments",
  CLIENT_PAYMENT_METHODS: "/client/payment-methods",
  CLIENT_RECURRING_PROFILES: "/client/recurring-profiles",
  CLIENT_PROMOTIONS: "/client/promotions",
  CLIENT_REGISTER: "/client/register",
  PROMOTIONS: "/promotions",
  PERSONAL_DATA_REPORT: "/personal-data-report",
  INVOICE_PAY: "/invoice/:id/pay",
  INVOICE_PAY_LATER: "/client/pay-later/:id",
  INVOICE_DETAILS: "/invoice/:id",
  CANCEL_BOOKING: "/client/cancel-booking",
  LOGIN: "/login",
  ORDER_SUMMARY: "/order-summary",
  CLIENT_RESTORE_PASSWORD: "/client/restore-password",
  TERMS_AND_CONDITIONS: "/terms-and-conditions",
  ONLINE_MEETING: "/online-meeting/:id",
  MEMBERSHIPS: "/memberships",
  CLIENT_MEMBERSHIPS: "/client/memberships",
  CLIENT_RECURRING_PROFILE: "/client/recurring-profile/:id",
  MEMBERSHIPS_CHOOSE: "/memberships/choose",
  PACKAGES: "/packages",
  CLIENT_WAITING_LIST: "/client/waiting-list",
  WAITING_LIST_DETAILS: "/waiting-list/details",
  WAITING_LIST_JOINED: "/waiting-list/status/joined",
};

export const BOOKING_STATUS = {
  PENDING: "pending",
  CONFIRMED: "confirmed",
  CANCELLED: "cancelled",
  DELAY: "delay",
  ERROR: "error",
  PENDING_DELAY: "pending_delay",
  PENDING_PAYMENT: "pending_payment",
  RESCHEDULE_SUCCESS: "reschedule_success"
};

export const WAITING_LIST_STATUS = {
  WAITING: "waiting",
  SLOT_AVAILABLE: "slot_available",
  CANCELLED: "cancelled",
  BOOKED: "booked",
};

export const INVOICE_STATUS = {
  PAID: "paid",
  PENDING: "pending",
  NEW: "new",
  ERROR: "error",
  CANCELLED: "cancelled",
  CANCELLED_BY_TIMEOUT: "cancelled_by_timeout",
  REFUNDED: "refunded",
};

export const RECURRING_PROFILE_STATUS = {
  ACTIVE: "active",
  ERROR: "error",
  CANCELLED: "cancelled",
  PENDING: "pending",
  SUSPENDED: "suspended",
};

export const RECURRING_PROFILE_STATUS_COLOR = {
  [RECURRING_PROFILE_STATUS.ACTIVE]: "success",
  [RECURRING_PROFILE_STATUS.ERROR]: "error",
  [RECURRING_PROFILE_STATUS.CANCELLED]: "neutral",
  [RECURRING_PROFILE_STATUS.PENDING]: "neutral",
  [RECURRING_PROFILE_STATUS.SUSPENDED]: "warning",
};

export const getClientSessionTokenKey = (companyLogin) => `${companyLogin}_sb_client_token`;
export const SESSION_STORAGE_CART = "sb_cart";
export const SESSION_STORAGE_DIRECT_ORDER = "sb_direct_order";
export const SESSION_STORAGE_MEMBERSHIP_CALLBACK = "sb_membership_callback";
export const SESSION_STORAGE_PENDING_PAYMENT = "sb_pending_payment";

export const RECURRING_SESSIONS_LABEL_TEXT = {
  SINGLE: "session",
  MULTIPLE: "sessions",
};

export const CLIENT_PROMO_UNSUBSCRIBE_KEYS = {
  EMAIL: "is_email_unsubscribed",
  SMS: "is_sms_unsubscribed",
};

export const CLIENT_REGISTER_POLICY_FIELDS_KEYS = {
  CANCELLATION_TERMS: "cancellation_terms",
  PROMOTION_LETTERS: "promotion_letters",
  SIMPLYBOOK_TERMS: "simplybook_terms",
  USER_TERMS: "user_terms",
  PRIVACY_POLICY: "privacy_policy",
};

export const TOAST_ERROR_MESSAGE = {
  DEFAULT: "An error occurred. Please try again later.",
  BOOKING: "An error occurred while booking. Please try again later.",
  MULTIPLE_BOOKING_DISABLED: "You can only select one service.",
  UNCOMBINABLE_SERVICE_IS_SELECTED: "The selected service cannot be combined with other services.",
  PRESELECTION_FAILED: "Some preselected items could not be applied.",
  PAYMENT_METHOD_UNSUPPORTED: "This payment method is not supported here. Please try again from the checkout page.",
};

export const PAYMENT_PROCESSORS_ICONS_PATH = "/v2/themes/assets/img/payments/";

export const WIDGET_CALLBACK_URL = "sb-widget-callback";

export const DAYS_OF_WEEK_MON_FIRST = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
export const DAYS_OF_WEEK_SUN_FIRST = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export const MONTHS = [
  { value: 1, label: "_January_" },
  { value: 2, label: "_February_" },
  { value: 3, label: "_March_" },
  { value: 4, label: "_April_" },
  { value: 5, label: "_May_" },
  { value: 6, label: "_June_" },
  { value: 7, label: "_July_" },
  { value: 8, label: "_August_" },
  { value: 9, label: "_September_" },
  { value: 10, label: "_October_" },
  { value: 11, label: "_November_" },
  { value: 12, label: "_December_" },
];

export const BATCH_CANCEL_MODES = {
  RECURRING_BATCH: "recurring_batch",
  MULTIPLE_BATCH: "multiple_batch",
};

export const BOOKING_BATCH_TYPES = {
  RECURRING: "recurring",
  MULTIPLE: "multiple",
  GROUP: "group",
};

export const DEFAULT_THEME = {
  colors: {
    accent: "#183046",
    accentContrast: "#ffffff",
    text: "#0b3052",
    textSecondary: "#6d7785",
    border: "#e7eaee",
    background: "#ffffff",
    link: "#216fec",
    error: "#ef4444",
    warning: "#f59e0b",
    success: "#22c55e",
    iconStroke: "#ABBCCC"
  },
  fontSizes: {
    h2: "1.25rem",
    h3: "1.125rem",
    p: "1rem",
  },
  borderRadius: {
    sm: "8px",
    md: "16px",
  },
};

export const WIDGET_CONTAINER_NAME = "sb-widget";

export const CLIENT_LOGIN_VIEW_STATES = {
  LOGIN: "login",
  FORGOT_PASSWORD: "forgot-password",
};

export const AUTH_EVENTS = {
  SESSION_EXPIRED: "sb-widget-session-expired",
};

export const CART_LINE_TYPES = {
  PRODUCT: "product",
  MEMBERSHIP: "membership",
  PROMOTION: "promotion",
  PACKAGE: "package",
};

export const CART_LINE_TYPE_LABELS = {
  [CART_LINE_TYPES.PRODUCT]: "Product",
  [CART_LINE_TYPES.PROMOTION]: "Gift Card",
  [CART_LINE_TYPES.MEMBERSHIP]: "Membership",
  [CART_LINE_TYPES.PACKAGE]: "Package",
};

export const CART_LINE_ID_FIELD = {
  [CART_LINE_TYPES.PRODUCT]: "product_id",
  [CART_LINE_TYPES.MEMBERSHIP]: "membership_id",
  [CART_LINE_TYPES.PACKAGE]: "package_id",
  [CART_LINE_TYPES.PROMOTION]: "promotion_id",
};

export const DISCOUNT_TYPE = {
  FIXED: "fixed_amount",
  PERCENTAGE: "percentage",
};
